import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { ErrorMessage, PageLoader, UsageBar } from '@/components/ui'
import { api } from '@/lib/api'
import {
  ROLE_TITLES,
  USAGE_METRIC_TITLES,
  type DashboardListResponse,
  type DatasetListResponse,
  type UsageResponse,
} from '@/lib/types'
import { useActiveWorkspace, useAuthStore, useHasPermission } from '@/stores/auth'

/**
 * Главный экран рабочего пространства.
 *
 * Пока дашбордов нет, экран честно показывает текущее состояние и следующий
 * шаг, а не изображает пустые виджеты. Заглушки, похожие на работающие
 * функции, — худший вид интерфейса: пользователь нажимает и ничего не
 * происходит. Поэтому шаги, до которых разработка не дошла, помечены «скоро»,
 * а пройденные отмечаются по фактическому состоянию данных.
 */
export function WorkspaceHomePage() {
  const workspace = useActiveWorkspace()
  const user = useAuthStore((state) => state.user)
  const canViewUsage = useHasPermission('usage:view')

  const usage = useQuery({
    queryKey: ['usage', workspace?.workspace_id],
    queryFn: () => api.get<UsageResponse>('/usage'),
    enabled: Boolean(workspace) && canViewUsage,
  })

  const datasets = useQuery({
    queryKey: ['datasets', workspace?.workspace_id],
    queryFn: () => api.get<DatasetListResponse>('/datasets'),
    enabled: Boolean(workspace),
  })

  const dashboards = useQuery({
    queryKey: ['dashboards', workspace?.workspace_id],
    queryFn: () => api.get<DashboardListResponse>('/dashboards'),
    enabled: Boolean(workspace),
  })

  if (!workspace) {
    return <PageLoader label="Загружаем рабочее пространство…" />
  }

  const hasData = (datasets.data?.total ?? 0) > 0
  const hasDashboards = (dashboards.data?.total ?? 0) > 0

  const firstName = user?.full_name?.split(' ')[0]

  return (
    <div className="space-y-6">
      <section className="card p-6">
        <h2 className="text-lg font-semibold text-slate-900">
          {firstName ? `${firstName}, добро пожаловать` : 'Добро пожаловать'}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Рабочее пространство <strong>{workspace.name}</strong> создано. Дальше — подключение
          данных: загрузка выгрузки из Excel или CSV.
        </p>

        <ol className="mt-5 space-y-3">
          <Step done title="Создать рабочее пространство" description="Готово" />
          <Step
            done={hasData}
            title="Загрузить XLSX или CSV"
            description="Система разберёт структуру файла, определит типы колонок и покажет проблемы качества данных"
          />
          <Step
            done={datasets.data?.items.some((item) => item.mapping_confirmed) === true}
            title="Подтвердить смысл колонок"
            description="Где дата, где выручка, где клиент — подтверждаете вы, а не угадывает система"
          />
          <Step
            done={hasDashboards}
            title="Собрать дашборд"
            description="Система предлагает показатели по вашим данным, вы принимаете нужные"
          />
          <Step
            title="Описать цель словами"
            description="Формулируете задачу обычным языком, система предлагает виджеты — вы принимаете или отклоняете"
            soon
          />
        </ol>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link to={hasData ? '/dashboards' : '/sources'} className="btn-primary">
            {hasData ? 'К дашбордам' : 'Загрузить первый файл'}
          </Link>
          {hasData && (
            <Link to="/sources" className="btn-secondary">
              Источники данных
            </Link>
          )}
          {!hasData && (
            <span className="text-xs text-slate-500">
              Нет файла под рукой — на той же странице есть демо-выгрузка продаж
            </span>
          )}
        </div>

        <p className="mt-5 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
          Шаг, отмеченный как «скоро», появится на следующем этапе разработки: сейчас работают
          аккаунты и роли, загрузка данных, семантический слой, дашборды и расчёт показателей.
        </p>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card p-6">
          <h3 className="text-base font-semibold text-slate-900">Рабочее пространство</h3>
          <dl className="mt-4 space-y-3 text-sm">
            <Row label="Название" value={workspace.name} />
            <Row label="Идентификатор" value={workspace.slug} mono />
            <Row label="Ваша роль" value={ROLE_TITLES[workspace.role]} />
          </dl>
          <Link to="/settings" className="btn-secondary mt-4">
            Настройки пространства
          </Link>
        </section>

        {canViewUsage && (
          <section className="card p-6">
            <h3 className="text-base font-semibold text-slate-900">Тариф и лимиты</h3>

            {usage.isLoading && <p className="mt-4 text-sm text-slate-500">Загружаем…</p>}
            <ErrorMessage error={usage.error} />

            {usage.data && (
              <>
                <p className="mt-1 text-sm text-slate-600">
                  Текущий тариф: <strong>{usage.data.plan_name}</strong>
                </p>
                <dl className="mt-4 space-y-2.5">
                  {Object.entries(usage.data.metrics)
                    .filter(([metric]) => metric in USAGE_METRIC_TITLES)
                    .map(([metric, value]) => (
                      <div key={metric} className="flex items-center justify-between gap-3 text-sm">
                        <dt className="text-slate-600">{USAGE_METRIC_TITLES[metric] ?? metric}</dt>
                        <dd>
                          <UsageBar used={value.used} limit={value.limit} />
                        </dd>
                      </div>
                    ))}
                </dl>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  )
}

function Step({
  title,
  description,
  done = false,
  soon = false,
}: {
  title: string
  description: string
  done?: boolean
  soon?: boolean
}) {
  return (
    <li className="flex gap-3">
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
          done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
        }`}
        aria-hidden="true"
      >
        {done ? '✓' : '·'}
      </span>
      <span>
        <span className="flex items-center gap-2 text-sm font-medium text-slate-900">
          {title}
          {soon && <span className="badge bg-slate-100 text-[10px] text-slate-500">скоро</span>}
        </span>
        <span className="block text-sm text-slate-500">{description}</span>
      </span>
    </li>
  )
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`text-slate-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  )
}
